import { Link } from "react-router-dom";
import useSEO from '@/hooks/useSEO';
import { comparisonPreviews, compareStats, compareMethods, faqData, brokerIntroductions } from "@/mocks/compareHubData";
import { useScrollReveal, getRevealClasses } from "@/hooks/useScrollReveal";
import { useState } from "react";
import { brokers } from "@/data/brokers";

/* ------------------------------------------------------------------ */
/*  Hero                                                               */
/* ------------------------------------------------------------------ */

function HeroSection() {
  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-[#0B0F19]">
      {/* Background glow effects */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#7C3AED]/8 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#10B981]/5 rounded-full blur-[120px]" />

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
          backgroundSize: "60px 60px"
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center max-w-4xl mx-auto mb-10 md:mb-14">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7C3AED]/10 border border-[#7C3AED]/20 mb-6">
            <i className="ri-scales-3-line text-[#8B5CF6]"></i>
            <span className="text-xs font-bold text-[#8B5CF6] uppercase tracking-wider">Broker Comparisons</span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-[#F8FAFC] mb-6 tracking-tight leading-tight">
            Compare XM Against{" "}
            <span className="text-gradient">Every Major Broker</span>
          </h1>
          <p className="text-base md:text-lg text-[#94A3B8] max-w-2xl mx-auto leading-relaxed">
            Data-driven head-to-head comparisons across 8 critical dimensions. No marketing fluff, no affiliate bias — 
            just real numbers from real accounts so you can pick the broker that actually fits your trading style.
          </p>

          {/* Quick jump pills */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            {comparisonPreviews.map((broker) => (
              <a
                key={broker.slug}
                href={broker.slug}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1F2937] border border-[#374151] hover:border-[#7C3AED]/50 text-sm text-[#94A3B8] hover:text-[#F8FAFC] transition-all whitespace-nowrap"
              >
                <img src={broker.brokerLogo} alt={broker.brokerName} className="w-5 h-5 rounded-full object-cover" />
                XM vs {broker.brokerName}
              </a>
            ))}
          </div>
        </div>

        {/* Stats Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {compareStats.map((stat) => (
            <div
              key={stat.label}
              className="bg-[#111827]/80 border border-[#1F2937] rounded-xl p-5 md:p-6 text-center hover:border-[#374151] transition-all"
            >
              <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-[#7C3AED]/10 flex items-center justify-center">
                <i className={`${stat.icon} text-lg text-[#8B5CF6]`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-black text-[#F8FAFC] mb-1">{stat.value}</div>
              <div className="text-xs text-[#64748B] leading-tight">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Broker Battle Cards                                                */
/* ------------------------------------------------------------------ */

function BrokerBattleCards() {
  const { ref, isVisible } = useScrollReveal(0.1);

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#111827] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 mb-5">
            <i className="ri-contrast-2-line text-[#F59E0B]"></i>
            <span className="text-xs font-bold text-[#F59E0B] uppercase tracking-wider">Head-to-Head</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">
            XM vs The Competition
          </h2>
          <p className="text-[#94A3B8] text-sm md:text-base max-w-2xl mx-auto">
            Five brokers. Five distinct philosophies. See at a glance how XM stacks up across the metrics that actually matter to real traders — 
            then dive into the full 8-module analysis for any matchup.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-5">
          {comparisonPreviews.map((broker, index) => (
            <div key={broker.slug} className={index < 2 ? "lg:col-span-3" : "lg:col-span-2"}>
              <BattleCard broker={broker} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BattleCard({ broker }: { broker: typeof comparisonPreviews[0] }) {
  const xmScore = broker.quickStats.filter((s) => s.xmWins).length;
  const rivalScore = broker.quickStats.filter((s) => !s.xmWins).length;

  return (
    <Link
      to={broker.slug}
      className="group block relative bg-[#1A2235] border border-[#2A3345] rounded-2xl overflow-hidden hover:border-[#7C3AED]/40 transition-all duration-500 hover:-translate-y-1"
    >
      {/* Subtle top glow */}
      <div className="absolute top-0 inset-x-0 h-[1px] bg-gradient-to-r from-transparent via-[#7C3AED]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="p-6 md:p-7 flex flex-col h-full">
        {/* VS Header */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/15 border border-[#7C3AED]/25 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
            <img src="/images/xm.jpg" alt="XM" className="w-8 h-8 rounded-lg object-cover" />
          </div>
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#10B981] flex items-center justify-center">
              <i className="ri-swap-line text-white text-xs"></i>
            </div>
            <span className="text-[9px] text-[#475569] mt-0.5 uppercase tracking-widest">vs</span>
          </div>
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300"
            style={{ backgroundColor: `${broker.brokerColor}15`, borderColor: `${broker.brokerColor}30`, borderWidth: 1, borderStyle: "solid" }}
          >
            <img src={broker.brokerLogo} alt={broker.brokerName} className="w-8 h-8 rounded-lg object-cover" />
          </div>
        </div>

        {/* Name + Battle Tag */}
        <div className="text-center mb-5">
          <h3 className="text-base font-bold text-[#F8FAFC] group-hover:text-[#8B5CF6] transition-colors mb-1">
            XM vs {broker.brokerName}
          </h3>
          <span
            className="inline-block text-[10px] font-bold px-2.5 py-1 rounded-full"
            style={{ backgroundColor: `${broker.brokerColor}12`, color: broker.brokerColor }}
          >
            {broker.battleTag}
          </span>
        </div>

        {/* Score Tally */}
        <div className="bg-[#111827] rounded-xl p-4 mb-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] text-[#64748B] uppercase tracking-wider">Quick Stats Scorecard</span>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold text-[#8B5CF6]">{xmScore}</span>
              <span className="text-[9px] text-[#475569]">-</span>
              <span className="text-[10px] font-bold" style={{ color: broker.brokerColor }}>{rivalScore}</span>
            </div>
          </div>

          {/* Score bar */}
          <div className="flex h-1.5 rounded-full overflow-hidden bg-[#1F2937] mb-4">
            <div
              className="bg-[#8B5CF6] transition-all duration-500"
              style={{ width: `${(xmScore / (xmScore + rivalScore)) * 100}%` }}
            />
            <div
              className="transition-all duration-500"
              style={{ width: `${(rivalScore / (xmScore + rivalScore)) * 100}%`, backgroundColor: broker.brokerColor }}
            />
          </div>

          {/* Individual metrics */}
          <div className="space-y-2.5">
            {broker.quickStats.map((stat) => (
              <div key={stat.label} className="flex items-center gap-2">
                <span className="text-[10px] text-[#64748B] w-20 shrink-0 truncate">{stat.label}</span>
                <div className="flex-1 flex items-center gap-1.5">
                  <div className="flex-1 h-1 rounded-full bg-[#1F2937] overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${stat.xmWins ? 'bg-[#8B5CF6]' : 'bg-[#374151]'}`}
                      style={{ width: stat.xmWins ? '100%' : '35%' }}
                    />
                  </div>
                  <span className={`text-[9px] font-mono font-bold ${stat.xmWins ? 'text-[#8B5CF6]' : 'text-[#475569]'}`}>
                    {stat.xm}
                  </span>
                  <span className="text-[9px] text-[#374151]">/</span>
                  <span className={`text-[9px] font-mono font-bold ${!stat.xmWins ? 'text-[#10B981]' : 'text-[#475569]'}`}>
                    {stat.rival}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Verdict snippet */}
        <p className="text-[11px] text-[#94A3B8] leading-relaxed mb-5 flex-1 line-clamp-3">
          "{broker.verdict}"
        </p>

        {/* CTA */}
        <div className="flex items-center justify-between pt-4 border-t border-[#2A3345]">
          <span className="text-[10px] text-[#64748B] font-medium">8 modules · Interactive Quiz</span>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#8B5CF6] group-hover:text-[#A78BFA] transition-colors whitespace-nowrap">
            Full Analysis
            <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform"></i>
          </span>
        </div>
      </div>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/*  Broker Introductions                                               */
/* ------------------------------------------------------------------ */

function BrokerIntroductions() {
  const { ref, isVisible } = useScrollReveal(0.1);

  return (
    <section ref={ref} id="broker-introductions" className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 mb-5">
            <i className="ri-building-2-line text-[#10B981]"></i>
            <span className="text-xs font-bold text-[#10B981] uppercase tracking-wider">Meet The Contenders</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">Know Your Brokers Before You Compare</h2>
          <p className="text-[#94A3B8] text-sm md:text-base max-w-2xl mx-auto">
            Every broker has a story, a philosophy, and a unique personality. Get to know each one before you put them head-to-head.
          </p>
        </div>

        <div className="space-y-4 md:space-y-5">
          {brokerIntroductions.map((broker) => (
            <IntroCard key={broker.slug} broker={broker} />
          ))}
        </div>
      </div>
    </section>
  );
}

function IntroCard({ broker }: { broker: typeof brokerIntroductions[0] }) {
  return (
    <div
      className="group relative bg-[#111827] border border-[#1F2937] rounded-2xl overflow-hidden hover:border-[#374151] transition-all duration-500"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Left - Identity + Description */}
        <div className="lg:col-span-5 p-5 md:p-6 flex flex-col"
        >
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform duration-300"
              style={{ backgroundColor: `${broker.color}15`, borderColor: `${broker.color}30`, borderWidth: 1, borderStyle: "solid" }}
            >
              <img src={broker.logo} alt={broker.name} className="w-8 h-8 rounded-lg object-cover" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-[#F8FAFC] group-hover:text-[#10B981] transition-colors truncate">
                {broker.name}
              </h3>
              <span
                className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${broker.color}12`, color: broker.color }}
              >
                {broker.tagline}
              </span>
            </div>
          </div>

          <p className="text-xs text-[#94A3B8] leading-relaxed line-clamp-3 flex-1">
            {broker.description}
          </p>
        </div>

        {/* Middle - Stats */}
        <div className="lg:col-span-4 p-5 md:p-6 flex flex-col justify-center border-y lg:border-y-0 lg:border-x border-[#1F2937]"
        >
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 mb-4">
            <div>
              <div className="text-[9px] text-[#64748B] uppercase tracking-wider">Founded</div>
              <div className="text-xs font-bold text-[#F8FAFC]">{broker.founded}</div>
            </div>
            <div>
              <div className="text-[9px] text-[#64748B] uppercase tracking-wider">Min Deposit</div>
              <div className="text-xs font-bold text-[#10B981]">{broker.minDeposit}</div>
            </div>
            <div>
              <div className="text-[9px] text-[#64748B] uppercase tracking-wider">Max Leverage</div>
              <div className="text-xs font-bold text-[#F59E0B]">{broker.maxLeverage}</div>
            </div>
            <div>
              <div className="text-[9px] text-[#64748B] uppercase tracking-wider">Headquarters</div>
              <div className="text-xs font-bold text-[#F8FAFC] truncate">{broker.headquarters}</div>
            </div>
          </div>

          <div className="mb-2.5">
            <div className="text-[9px] text-[#64748B] uppercase tracking-wider mb-1">Regulation</div>
            <div className="flex flex-wrap gap-1">
              {broker.regulation.map((reg) => (
                <span
                  key={reg}
                  className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#1A2235] text-[#94A3B8] border border-[#2A3345]"
                >
                  {reg}
                </span>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[9px] text-[#64748B] uppercase tracking-wider mb-1">Platforms</div>
            <div className="flex flex-wrap gap-1">
              {broker.platforms.map((plat) => (
                <span
                  key={plat}
                  className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#1A2235] text-[#94A3B8] border border-[#2A3345]"
                >
                  {plat}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Right - Strengths + CTA */}
        <div className="lg:col-span-3 p-5 md:p-6 flex flex-col justify-between"
        >
          <div className="mb-4">
            <div className="text-[9px] text-[#64748B] uppercase tracking-wider mb-2">Key Strengths</div>
            <ul className="space-y-1.5">
              {broker.strengths.map((s, i) => (
                <li key={i} className="flex gap-2 text-[11px] text-[#94A3B8] leading-relaxed">
                  <span className="mt-0.5 shrink-0" style={{ color: broker.color }}>
                    <i className="ri-check-double-line text-xs"></i>
                  </span>
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <a
            href={broker.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-[#1F2937] hover:bg-[#374151] border border-[#374151] hover:border-[#475569] text-[#F8FAFC] text-xs font-semibold rounded-lg transition-all whitespace-nowrap"
          >
            Open Account Now
            <i className="ri-arrow-right-up-line"></i>
          </a>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  How We Compare                                                     */
/* ------------------------------------------------------------------ */

function HowWeCompare() {
  const { ref, isVisible } = useScrollReveal(0.1);

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#111827] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 mb-5">
            <i className="ri-magic-line text-[#10B981]"></i>
            <span className="text-xs font-bold text-[#10B981] uppercase tracking-wider">Our Methodology</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">How We Compare Brokers</h2>
          <p className="text-[#94A3B8] text-sm md:text-base max-w-2xl mx-auto">
            We don't copy-paste broker marketing pages. Every data point is verified through live trading accounts using our own money.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {compareMethods.map((method) => (
            <div
              key={method.title}
              className="group bg-[#1A2235] border border-[#2A3345] rounded-xl p-6 md:p-7 hover:border-[#7C3AED]/30 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/10 flex items-center justify-center shrink-0 group-hover:bg-[#7C3AED]/20 transition-colors">
                  <i className={`${method.icon} text-xl text-[#8B5CF6]`}></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#F8FAFC] mb-2">{method.title}</h3>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{method.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust badge row */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-6 md:gap-10">
          <div className="flex items-center gap-2 text-xs text-[#64748B]">
            <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
            Real Money Tested
          </div>
          <div className="flex items-center gap-2 text-xs text-[#64748B]">
            <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
            Updated Quarterly
          </div>
          <div className="flex items-center gap-2 text-xs text-[#64748B]">
            <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
            Zero Affiliate Bias
          </div>
          <div className="flex items-center gap-2 text-xs text-[#64748B]">
            <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
            Community Verified
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  FAQ                                                                */
/* ------------------------------------------------------------------ */

function FaqSection() {
  const { ref, isVisible } = useScrollReveal(0.1);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 mb-5">
            <i className="ri-question-answer-line text-[#F59E0B]"></i>
            <span className="text-xs font-bold text-[#F59E0B] uppercase tracking-wider">FAQ</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-3">Common Questions About Broker Comparisons</h2>
          <p className="text-[#94A3B8] text-sm">Everything you need to know before diving into the comparisons.</p>
        </div>

        <div className="space-y-3">
          {faqData.map((item, i) => (
            <div
              key={i}
              className="bg-[#111827] border border-[#1F2937] rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-[#1A2235]/50 transition-colors"
              >
                <span className="text-sm font-semibold text-[#F8FAFC] pr-4">{item.q}</span>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                  openIndex === i ? "bg-[#7C3AED] rotate-45" : "bg-[#1F2937]"
                }`}>
                  <i className={`${openIndex === i ? "ri-close-line text-white" : "ri-add-line text-[#64748B]"} text-sm`}></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === i ? "max-h-[300px] pb-5 px-5" : "max-h-0"
                }`}
              >
                <p className="text-sm text-[#94A3B8] leading-relaxed">{item.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Bottom CTA                                                         */
/* ------------------------------------------------------------------ */

function BottomCTA() {
  return (
    <section className="py-20 md:py-28 bg-[#111827]">
      <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7C3AED]/10 border border-[#7C3AED]/20 mb-6">
          <i className="ri-rocket-line text-[#8B5CF6]"></i>
          <span className="text-xs font-bold text-[#8B5CF6] uppercase tracking-wider">Start Trading</span>
        </div>
        <h2 className="text-3xl md:text-5xl font-bold text-[#F8FAFC] mb-4">
          Still Not Sure? Dive Into The Data
        </h2>
        <p className="text-[#94A3B8] text-sm md:text-base mb-8 max-w-xl mx-auto">
          Every comparison page is packed with real account data, interactive quizzes, and honest verdicts. Pick any opponent below — or open an XM account and experience it yourself.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={brokers.xm.href}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white text-sm font-bold rounded-lg transition-all hover:shadow-xl hover:shadow-[#7C3AED]/25 whitespace-nowrap"
          >
            Open XM Account
            <i className="ri-arrow-right-up-line"></i>
          </a>
          <a
            href="#broker-introductions"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#1F2937] border border-[#374151] hover:border-[#7C3AED] text-[#F8FAFC] text-sm font-bold rounded-lg transition-all whitespace-nowrap"
          >
            Browse All Comparisons
            <i className="ri-arrow-down-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Full Page                                                          */
/* ------------------------------------------------------------------ */

export default function CompareHub() {
  useSEO({
    /* @replace:compare-title */

    title: 'Compare XM Brokers 2026 | XM vs Tickmill, FxPro & More',
    /* @replace:compare-description */

    description: 'Explore XM broker comparisons and see how XM differs from other forex brokers. Compare trading conditions, platforms, fees, accounts, and key features across multiple broker comparisons.',
    /* @replace:compare-keywords */

    keywords: 'XM comparisons, XM vs brokers, XM vs Tickmill, XM vs FxPro, XM vs IC Markets, XM broker comparison',
    /* @replace:compare-canonical */

    canonical: '/compare',
  });

  return (
    <div className="min-h-screen bg-[#111827]">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Compare XM Against Every Major Forex Broker 2026",
        "description": "Data-driven XM broker comparison hub. Compare XM against Tickmill, FxPro, IC Markets, Axi, and FXTM across 8 critical dimensions. Real account testing, no affiliate bias.",
        "keywords": "XM comparison,XM vs Tickmill,XM vs IC Markets,XM vs FxPro,forex broker comparison,best forex broker 2026",
      }) }} />

      <HeroSection />
      <BrokerBattleCards />
      <BrokerIntroductions />
      <HowWeCompare />
      <FaqSection />
      <BottomCTA />
    </div>
  );
}